/**
 * What the user sees after uploading a fuel CSV.
 *
 * The import used to finish silently, so people could not tell a clean upload
 * from one that skipped rows and simply uploaded the file again (which is how
 * duplicates appeared). The panel says how many rows were added / updated /
 * skipped, whether any were converted from a foreign currency, and — per
 * error code — what went wrong and what to do about it.
 */
import React from "react";
import { useTranslation } from "react-i18next";
import clsx from "clsx";
import {
  AlertTriangle,
  ArrowRightLeft,
  CheckCircle2,
  Info,
  XCircle,
  X,
} from "lucide-react";
import type {
  FuelImportOutcome,
  FuelImportResponse,
  FuelImportRowError,
} from "types/FuelImport";

/** Row numbers listed per error line before collapsing the rest into "and N more". */
export const MAX_LISTED_ROWS = 10;
/** Distinct free-text error lines shown before collapsing the rest into a counter. */
export const MAX_GENERIC_LINES = 5;

type Tone = "success" | "warning" | "error";
type LineTone = "warning" | "info";

const TONE_STYLES: Record<Tone, { box: string; icon: string }> = {
  success: {
    box: "border-green-200 bg-green-50 text-green-900",
    icon: "text-green-600",
  },
  warning: {
    box: "border-amber-200 bg-amber-50 text-amber-900",
    icon: "text-amber-600",
  },
  error: {
    box: "border-red-200 bg-red-50 text-red-900",
    icon: "text-red-600",
  },
};

const TONE_ICONS = {
  success: CheckCircle2,
  warning: AlertTriangle,
  error: XCircle,
} as const;

/** One line of the details list: a message plus how loudly to show it. */
interface DetailLine {
  key: string;
  tone: LineTone;
  text: string;
}

const toCount = (value: unknown): number => {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : 0;
};

/** A usable row number, or NaN (`Number(null)` would otherwise pass for 0). */
const toRowNumber = (value: unknown): number =>
  value === null || value === undefined || value === "" ? NaN : Number(value);

/** Sorted, de-duplicated, numeric row numbers of a group of errors. */
const rowNumbers = (items: FuelImportRowError[]): number[] =>
  Array.from(
    new Set(
      items.map((e) => toRowNumber(e.row)).filter((n) => Number.isFinite(n))
    )
  ).sort((a, b) => a - b);

/** The backend sends a date or a full ISO timestamp; the calendar day is what matters here. */
const toDay = (date: string): string => date.slice(0, 10);

type Translate = ReturnType<typeof useTranslation>["t"];

const formatRows = (items: FuelImportRowError[], t: Translate): string => {
  const rows = rowNumbers(items);
  const listed = rows.slice(0, MAX_LISTED_ROWS).join(", ");
  const rest = rows.length - MAX_LISTED_ROWS;
  return rest > 0
    ? `${listed} ${t("fuel_import_rows_more", { count: rest })}`
    : listed;
};

/** "Rows: 4, 5" — or nothing at all when no error carries a row number. */
const rowsLabel = (
  items: FuelImportRowError[],
  t: Translate,
  key: "fuel_import_rows_label" | "fuel_import_rows_inline"
): string => {
  const rows = formatRows(items, t);
  return rows ? t(key, { rows }) : "";
};

const joinText = (...parts: string[]): string =>
  parts.filter(Boolean).join(" ");

/** "PLN 2026-09-30, PLN 2026-10-01": each currency + day the rate was missing for, once. */
const formatCurrencyDates = (items: FuelImportRowError[]): string => {
  const pairs = new Set<string>();
  items.forEach((e) => {
    const pair = [e.currency, e.date ? toDay(e.date) : ""]
      .filter(Boolean)
      .join(" ");
    if (pair) pairs.add(pair);
  });
  return Array.from(pairs).join(", ");
};

const groupBy = <T,>(
  items: T[],
  keyOf: (item: T) => string
): Map<string, T[]> => {
  const groups = new Map<string, T[]>();
  items.forEach((item) => {
    const key = keyOf(item);
    const group = groups.get(key);
    if (group) group.push(item);
    else groups.set(key, [item]);
  });
  return groups;
};

/** Turn the backend's error list into one explained line per kind of problem. */
function buildDetailLines(
  errors: FuelImportRowError[],
  t: Translate
): DetailLine[] {
  const byCode = (code: string) => errors.filter((e) => e.code === code);
  const knownCodes = [
    "CARD_NUMBER_DAMAGED",
    "FX_RATE_UNAVAILABLE",
    "SAVE_FAILED",
    "MISSING_CARD_OR_DATE",
  ];
  const lines: DetailLine[] = [];

  const damaged = byCode("CARD_NUMBER_DAMAGED");
  if (damaged.length) {
    lines.push({
      key: "damaged",
      tone: "warning",
      text: joinText(
        t("fuel_import_card_damaged"),
        rowsLabel(damaged, t, "fuel_import_rows_label")
      ),
    });
  }

  const fx = byCode("FX_RATE_UNAVAILABLE");
  if (fx.length) {
    const pairs = formatCurrencyDates(fx);
    lines.push({
      key: "fx",
      tone: "warning",
      text: joinText(
        pairs
          ? t("fuel_import_fx_unavailable", { pairs })
          : t("fuel_import_fx_unavailable_generic"),
        rowsLabel(fx, t, "fuel_import_rows_label")
      ),
    });
  }

  // Failed saves and codes this UI does not know yet: the backend's own text
  // is the only explanation there is, so show it, grouped by identical text.
  const generic = errors.filter(
    (e) => e.code === "SAVE_FAILED" || !knownCodes.includes(String(e.code))
  );
  const genericGroups = Array.from(
    groupBy(generic, (e) => `${e.code ?? ""}|${e.error ?? ""}`).values()
  );
  genericGroups.slice(0, MAX_GENERIC_LINES).forEach((group, i) => {
    const { code, error } = group[0];
    const text = error || t("fuel_import_unknown_error");
    lines.push({
      key: `generic-${i}`,
      tone: "warning",
      text: joinText(
        code === "SAVE_FAILED"
          ? t("fuel_import_save_failed", { error: text })
          : text,
        rowsLabel(group, t, "fuel_import_rows_inline")
      ),
    });
  });
  if (genericGroups.length > MAX_GENERIC_LINES) {
    lines.push({
      key: "generic-more",
      tone: "warning",
      text: t("fuel_import_more_errors", {
        count: genericGroups.length - MAX_GENERIC_LINES,
      }),
    });
  }

  const missing = byCode("MISSING_CARD_OR_DATE");
  if (missing.length) {
    const rows = formatRows(missing, t);
    lines.push({
      key: "missing",
      tone: "info",
      text: rows
        ? t("fuel_import_missing_card_or_date", { rows })
        : t("fuel_import_missing_card_or_date_unnumbered"),
    });
  }

  return lines;
}

function buildHeadline(result: FuelImportResponse, t: Translate): string {
  const updated = toCount(result.updated);
  const skipped = toCount(result.skipped);
  // The new-row count always leads, even at zero: a re-upload that only hit
  // rows already stored must read "0 new", not look like a successful import.
  const parts = [
    t("fuel_import_inserted", { count: toCount(result.inserted) }),
  ];
  if (updated > 0) parts.push(t("fuel_import_updated", { count: updated }));
  if (skipped > 0) parts.push(t("fuel_import_skipped", { count: skipped }));
  return t("fuel_import_headline", { parts: parts.join(", ") });
}

/**
 * Decide what to show for whatever the upload request resolved with. Only a
 * JSON object can be a summary. Anything else (empty 204, a proxy's HTML page)
 * means we cannot tell what happened, so it is shown as a failed upload rather
 * than claiming success, and so is `success: false` on an HTTP 200. An empty
 * message makes the panel use its generic "upload failed" text.
 */
export function toImportOutcome(data: unknown): FuelImportOutcome {
  if (!data || typeof data !== "object" || Array.isArray(data)) {
    return { kind: "failure", message: "" };
  }
  const result = data as FuelImportResponse;
  if (result.success === false) {
    const errors = Array.isArray(result.errors) ? result.errors : [];
    const first = errors.find(
      (e) => e && typeof e.error === "string" && e.error
    );
    return { kind: "failure", message: first?.error ?? "" };
  }
  return { kind: "result", result };
}

interface FuelImportResultProps {
  outcome: FuelImportOutcome;
  onDismiss: () => void;
}

const FuelImportResult: React.FC<FuelImportResultProps> = ({
  outcome,
  onDismiss,
}) => {
  const { t } = useTranslation();

  let tone: Tone;
  let headline: string;
  let converted = 0;
  let lines: DetailLine[] = [];

  if (outcome.kind === "failure") {
    tone = "error";
    headline = outcome.message
      ? t("fuel_import_failed", { message: outcome.message })
      : t("fuel_import_failed_generic");
  } else {
    const result = outcome.result ?? {};
    const errors = (Array.isArray(result.errors) ? result.errors : []).filter(
      (e): e is FuelImportRowError => !!e && typeof e === "object"
    );
    // Rows without a card number (commission fees and the like) are expected in
    // every card statement; anything else the user can act on.
    const needsAttention = errors.some(
      (e) => e.code !== "MISSING_CARD_OR_DATE"
    );
    tone = needsAttention ? "warning" : "success";
    headline = buildHeadline(result, t);
    converted = toCount(result.converted);
    lines = buildDetailLines(errors, t);
  }

  const styles = TONE_STYLES[tone];
  const ToneIcon = TONE_ICONS[tone];

  return (
    <div
      role={tone === "success" ? "status" : "alert"}
      className={clsx(
        "flex items-start gap-3 rounded-lg border p-4",
        styles.box
      )}
      data-testid="fuel-import-result"
      data-tone={tone}
    >
      <ToneIcon
        className={clsx("mt-0.5 h-5 w-5 shrink-0", styles.icon)}
        aria-hidden="true"
      />
      <div className="min-w-0 flex-1 space-y-2">
        <p className="text-sm font-semibold">{headline}</p>
        {converted > 0 && (
          <p className="flex items-start gap-2 text-sm">
            <ArrowRightLeft
              className="mt-0.5 h-4 w-4 shrink-0 text-gray-500"
              aria-hidden="true"
            />
            <span>{t("fuel_import_converted", { count: converted })}</span>
          </p>
        )}
        {lines.length > 0 && (
          <ul className="space-y-1.5">
            {lines.map((line) => (
              <li
                key={line.key}
                data-tone={line.tone}
                className={clsx(
                  "flex items-start gap-2 text-sm",
                  line.tone === "info" && "text-gray-600"
                )}
              >
                {line.tone === "info" ? (
                  <Info
                    className="mt-0.5 h-4 w-4 shrink-0 text-gray-400"
                    aria-hidden="true"
                  />
                ) : (
                  <AlertTriangle
                    className="mt-0.5 h-4 w-4 shrink-0 text-amber-600"
                    aria-hidden="true"
                  />
                )}
                <span className="min-w-0 break-words">{line.text}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
      <button
        type="button"
        onClick={onDismiss}
        aria-label={t("close")}
        className="-m-1 shrink-0 rounded p-1 max-md:-m-3 max-md:p-3 text-current opacity-60 hover:bg-black/5 hover:opacity-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-400"
      >
        <X className="h-4 w-4" aria-hidden="true" />
      </button>
    </div>
  );
};

export default FuelImportResult;
